import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi, publicConfigApi, type LoginSuccess } from '../services/api';
import { setAuthToken, setAuthTokenSilently, clearAuthStorage } from '@/lib/authStorage';
import { getOrCreateDeviceId } from '@/lib/deviceId';
import { trackEvent, setAnalyticsUser } from '@/lib/analytics';

interface User {
  id?: string;
  name: string;
  email: string;
  role?: 'user' | 'admin';
  totalXp?: number;
  level?: number;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  guestExamCount: number;
  guestExamLimit: number;
  isGuestSession: boolean;
}

/** Résultat de `login()` : soit connecté directement, soit un défi OTP (compte Admin). */
export type LoginOutcome =
  | { requiresOtp: false }
  | { requiresOtp: true; otpToken: string; message: string };

interface AuthActions {
  login: (email: string, password?: string) => Promise<LoginOutcome>;
  /** 2ème facteur (compte Admin) : code à 6 chiffres reçu par email. */
  verifyAdminOtp: (otpToken: string, code: string) => Promise<void>;
  signup: (
    name: string,
    email: string,
    password?: string,
    confirmPassword?: string,
  ) => Promise<{ requiresVerification: boolean }>;
  applySession: (token: string, user: User) => void;
  logout: () => void;
  incrementGuestExam: () => void;
  requiresAuth: () => boolean;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  loadGuestExamLimit: () => Promise<void>;
  ensureGuestSession: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      guestExamCount: 0,
      guestExamLimit: 2,
      isGuestSession: false,

      login: async (email, password) => {
        if (!password) {
          throw new Error('Password required');
        }
        const { data } = await authApi.login(email, password);
        if ('requiresOtp' in data && data.requiresOtp) {
          // Compte Admin : mot de passe validé, code OTP envoyé par email —
          // aucune session n'est ouverte tant que verifyAdminOtp() n'a pas
          // confirmé le code (2ème facteur).
          return { requiresOtp: true, otpToken: data.otpToken, message: data.message };
        }
        const success = data as LoginSuccess;
        setAuthToken(success.access_token);
        set({
          isAuthenticated: true,
          user: success.user as User,
          token: success.access_token,
          guestExamCount: 0,
        });
        setAnalyticsUser((success.user as User)?.id ?? null);
        trackEvent('login', { method: 'password' });
        return { requiresOtp: false };
      },

      verifyAdminOtp: async (otpToken, code) => {
        const { data } = await authApi.verifyAdminOtp(otpToken, code);
        setAuthToken(data.access_token);
        set({
          isAuthenticated: true,
          user: data.user as User,
          token: data.access_token,
          guestExamCount: 0,
        });
        setAnalyticsUser((data.user as User)?.id ?? null);
        trackEvent('login', { method: 'password_otp' });
      },

      signup: async (name, email, password, confirmPassword) => {
        if (!password || !confirmPassword) {
          throw new Error('Password required');
        }
        const { isGuestSession } = get();

        if (isGuestSession) {
          // Conversion d'un invité : la session courante est conservée, on
          // connecte directement (un email de confirmation est tout de même
          // envoyé côté backend).
          const { data } = await authApi.convertGuest(
            name,
            email,
            password,
            confirmPassword,
          );
          setAuthToken(data.access_token);
          set({
            isAuthenticated: true,
            isGuestSession: false,
            user: data.user as User,
            token: data.access_token,
            guestExamCount: 0,
          });
          setAnalyticsUser((data.user as User)?.id ?? null);
          trackEvent('sign_up', { method: 'guest_conversion' });
          return { requiresVerification: false };
        }

        // Inscription classique : aucun jeton renvoyé, l'utilisateur doit
        // confirmer son email avant de pouvoir se connecter.
        await authApi.register(name, email, password, confirmPassword);
        trackEvent('sign_up', { method: 'email' });
        return { requiresVerification: true };
      },

      applySession: (token, user) => {
        setAuthToken(token);
        set({
          isAuthenticated: true,
          isGuestSession: false,
          user,
          token,
          guestExamCount: 0,
        });
      },

      logout: () => {
        clearAuthStorage();
        set({ isAuthenticated: false, isGuestSession: false, user: null, token: null, guestExamCount: 0 });
        // Purge des données locales de l'utilisateur (historique, gamification,
        // paiement) — rien ne reste en cache après déconnexion. L'abonnement
        // vit côté serveur et sera re-hydraté au prochain login. Les préférences
        // d'appareil (thème, langue, onboarding) sont conservées. Imports
        // dynamiques pour éviter les cycles de modules (payment → auth).
        void Promise.all([
          import('@/store/usePracticeStore').then(({ usePracticeStore }) =>
            usePracticeStore.getState().clearHistory(),
          ),
          import('@/store/useGamificationStore').then(({ useGamificationStore }) =>
            useGamificationStore.getState().resetAll(),
          ),
          import('@/payment').then(({ purgePaymentLocalData }) => purgePaymentLocalData()),
        ]).catch(() => {});
      },

      incrementGuestExam: () => {
        set((s) => ({ guestExamCount: s.guestExamCount + 1 }));
      },

      requiresAuth: () => {
        const { isAuthenticated, guestExamCount, guestExamLimit } = get();
        return !isAuthenticated && guestExamCount >= guestExamLimit;
      },

      setUser: (user) => {
        set({ user, isAuthenticated: true });
      },

      setToken: (token) => {
        setAuthToken(token);
        set({ token, isAuthenticated: true });
      },

      loadGuestExamLimit: async () => {
        try {
          const { data } = await publicConfigApi.get();
          if (typeof data?.guestExamLimit === 'number') {
            set({ guestExamLimit: data.guestExamLimit });
          }
        } catch {
          // Keep default on failure
        }
      },

      ensureGuestSession: async () => {
        const { isAuthenticated, isGuestSession } = get();
        if (isAuthenticated || isGuestSession) return true;

        try {
          const deviceId = getOrCreateDeviceId();
          const { data } = await authApi.deviceGuest(deviceId);
          setAuthTokenSilently(data.access_token);
          set({
            isGuestSession: true,
            token: data.access_token,
            user: data.user as User,
          });
          return true;
        } catch {
          return false;
        }
      },
    }),
    { name: 'certexam-auth' }
  )
);
